import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    selectAllActiveActivityAudiences,
    selectError as selectActivityAudienceListError,
    selectIsLoaded as selectIsActivityAudienceListLoaded,
    selectIsLoading as selectIsActivityAudienceListLoading,
} from '../store/slices/activityAudiencesSlice';
import { ActivityAudienceItemData } from '../interfaces/businessDataItem/activityAudienceItem';
import { AudienceType } from '../../smt/components/Activity/Common/Common';

/**
 * Programs that have specific audience restrictions.
 * Programs not in this list allow all audience types.
 */
const PROGRAMS_WITH_RESTRICTIONS = [
    'Commercial',
    'Partner Program',
    'Dedicated Cloud',
    'Marketing',
] as const;

/**
 * Mapping of program names to their allowed audience types.
 * This is the single source of truth for program-audience restrictions.
 */
const PROGRAM_AUDIENCE_MAP: Record<string, readonly AudienceType[]> = {
    Commercial: [
        AudienceType.Public,
        AudienceType.CommercialPrivate,
        AudienceType.CommercialSemiPrivate,
        AudienceType.Internal,
        AudienceType.InternalPublic,
        AudienceType.InternalPrivate,
    ],
    Marketing: [
        AudienceType.Public,
        AudienceType.Internal,
        AudienceType.InternalPublic,
        AudienceType.InternalPrivate,
    ],
    'Partner Program': [
        AudienceType.PartnerPublic,
        AudienceType.PartnerPublicUnlisted,
        AudienceType.PartnerPrivate,
        AudienceType.PartnerSemiPrivate,
        AudienceType.Internal,
        AudienceType.InternalPublic,
        AudienceType.InternalPrivate,
    ],
    'Dedicated Cloud': [
        AudienceType.CommercialPrivate,
        AudienceType.CommercialSemiPrivate,
        AudienceType.Internal,
        AudienceType.InternalPublic,
        AudienceType.InternalPrivate,
    ],
};

/**
 * Helper function to filter audiences based on program type.
 * Exported for use in tests and non-hook contexts.
 */
export const filterAudiencesByProgram = (
    activityAudienceList: ActivityAudienceItemData[],
    program: string,
): ActivityAudienceItemData[] => {
    // If program is not in the restricted list, allow all audiences
    if (!(PROGRAMS_WITH_RESTRICTIONS as readonly string[]).includes(program)) {
        return activityAudienceList;
    }

    const allowedAudiences = PROGRAM_AUDIENCE_MAP[program];
    if (!allowedAudiences) {
        return activityAudienceList;
    }

    return activityAudienceList.filter((activityAudience) =>
        (allowedAudiences as readonly string[]).includes(
            activityAudience.activity_audience,
        ),
    );
};

/**
 * Check if a program has audience restrictions
 */
export const programHasAudienceRestrictions = (program: string): boolean => {
    return (PROGRAMS_WITH_RESTRICTIONS as readonly string[]).includes(program);
};

/**
 * Get allowed audiences for a program.
 * Returns undefined if program has no restrictions (allows all audiences).
 */
export const getAllowedAudiencesForProgram = (
    program: string,
): readonly AudienceType[] | undefined => {
    return PROGRAM_AUDIENCE_MAP[program];
};

export interface UseFilteredAudiencesResult {
    /** Filtered list of audiences based on the selected program */
    filteredAudiences: ActivityAudienceItemData[];
    /** All active audiences from the store (unfiltered) */
    allAudiences: ActivityAudienceItemData[];
    /** Whether the audience list is currently loading */
    isLoading: boolean;
    /** Whether the audience list has been loaded */
    isLoaded: boolean;
    /** Error message if loading failed */
    error: string | null;
}

/**
 * Custom hook for filtering activity audiences based on program type.
 *
 * This hook provides a centralized, reusable way to get filtered audiences
 * based on the selected program. Use this hook in any component that needs
 * to display audience options filtered by program.
 *
 * @param program - The selected program type (e.g., 'Commercial', 'Partner Program')
 * @returns Object containing filtered audiences and loading state
 *
 * @example
 * ```tsx
 * const { filteredAudiences, isLoading, isLoaded, error } = useFilteredAudiences(formValues.program);
 *
 * // Use with getOptionsAndLookupForSelectInput
 * const { valueLookup, valueOptions } = useMemo(() =>
 *   getOptionsAndLookupForSelectInput(filteredAudiences, (a) => ({
 *     label: a.activity_audience,
 *     id: a.pk as string,
 *   })),
 * [filteredAudiences]);
 * ```
 */
export const useFilteredAudiences = (
    program: string,
): UseFilteredAudiencesResult => {
    const allAudiences = useSelector(selectAllActiveActivityAudiences);
    const isLoading = useSelector(selectIsActivityAudienceListLoading);
    const isLoaded = useSelector(selectIsActivityAudienceListLoaded);
    const error = useSelector(selectActivityAudienceListError);

    const filteredAudiences = useMemo(
        () => filterAudiencesByProgram(allAudiences, program),
        [allAudiences, program],
    );

    return {
        filteredAudiences,
        allAudiences,
        isLoading,
        isLoaded,
        error,
    };
};

export default useFilteredAudiences;
